"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CurrencyField = exports.getSymbolAlignStr = void 0;
const joi_1 = __importDefault(require("joi"));
const datasheet_1 = require("../datasheet");
const number_base_field_1 = require("./number_base_field");
const utils_1 = require("../../utils");
const field_types_1 = require("../../types/field_types");
const validate_schema_1 = require("../../model/field/validate_schema");
const types_1 = require("../../types");
const getSymbolAlignStr = (symbolAlign) => {
    if (symbolAlign == null) {
        return undefined;
    }
    const alignMap = ['Default', 'Left', 'Right'];
    if (![1, 2].includes(symbolAlign)) {
        symbolAlign = 0;
    }
    return alignMap[symbolAlign];
};
exports.getSymbolAlignStr = getSymbolAlignStr;
class CurrencyField extends number_base_field_1.NumberBaseField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    get apiMetaProperty() {
        const { defaultValue, precision, symbol, symbolAlign } = this.field.property;
        return {
            defaultValue: defaultValue || undefined,
            precision,
            symbol,
            symbolAlign: (0, exports.getSymbolAlignStr)(symbolAlign),
        };
    }
    // the data of preview, includes (positive or negative sign) + currency symbol + scientific notation number or normal number + thousand separator
    cellValueToString(cellValue, cellToStringOption) {
        if (this.validate(cellValue)) {
            const { symbol, precision, symbolAlign } = this.field.property;
            let cellString;
            cellString = (0, utils_1.numberToShow)(cellValue, precision);
            const { hideUnit } = cellToStringOption || {};
            if (!hideUnit) {
                cellString = (0, utils_1.str2Currency)(cellString, symbol, 3, field_types_1.DefaultCommaStyle, symbolAlign);
            }
            return cellString || null;
        }
        return null;
    }
    // return new records' default value of field attributes
    defaultValue() {
        const { defaultValue } = this.field.property;
        return defaultValue ? (0, utils_1.str2number)(defaultValue) : null;
    }
    compareCellValue(cellValue) {
        const cellValue2Str = this.cellValueToString(cellValue, { hideUnit: true });
        return cellValue2Str === null ? null : (0, utils_1.str2number)(cellValue2Str);
    }
    compare(cellValue1, cellValue2) {
        return number_base_field_1.NumberBaseField._compare(this.compareCellValue(cellValue1), this.compareCellValue(cellValue2));
    }
    validateProperty() {
        return CurrencyField.propertySchema.validate(this.field.property);
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: field_types_1.FieldType.Currency,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    static defaultProperty() {
        return {
            symbol: '$',
            precision: 2,
            symbolAlign: field_types_1.SymbolAlign.default
        };
    }
    get openFieldProperty() {
        const { defaultValue, symbol, precision, symbolAlign } = this.field.property;
        return {
            defaultValue,
            symbol,
            precision,
            symbolAlign: (0, exports.getSymbolAlignStr)(symbolAlign)
        };
    }
    validateUpdateOpenProperty(updateProperty) {
        return CurrencyField.openUpdatePropertySchema.validate(updateProperty);
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { defaultValue, symbolAlign: symbolAlignStr, symbol, precision } = openFieldProperty;
        const defaultProperty = CurrencyField.defaultProperty();
        const symbolAlign = symbolAlignStr === undefined ? defaultProperty.symbolAlign : field_types_1.SymbolAlign[symbolAlignStr.toLocaleLowerCase()];
        return {
            defaultValue,
            symbol: symbol || defaultProperty.symbol,
            precision: precision || defaultProperty.precision,
            symbolAlign: symbolAlign
        };
    }
}
exports.CurrencyField = CurrencyField;
CurrencyField.propertySchema = joi_1.default.object({
    symbol: joi_1.default.string().allow('').required(),
    precision: joi_1.default.number().integer().min(0).max(10).required(),
    defaultValue: joi_1.default.string().allow(''),
    symbolAlign: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(field_types_1.SymbolAlign)),
}).required();
CurrencyField.openUpdatePropertySchema = joi_1.default.object({
    symbol: joi_1.default.string().allow(''),
    precision: joi_1.default.number().integer().min(0).max(10),
    defaultValue: joi_1.default.string().allow(''),
    symbolAlign: joi_1.default.valid(...(0, validate_schema_1.enumToArray)(types_1.TSymbolAlign)),
}).required();
//# sourceMappingURL=currency_field.js.map